(() => {
  if (customElements.get('pp-product-addons')) return;

  class PPProductAddons extends HTMLElement {
    connectedCallback() {
      this.section = this.closest('.m-main-product');
      this.mainForm = document.getElementById(this.dataset.formId);
      this.rows = Array.from(this.querySelectorAll('[data-addon-row]'));
      if (!this.section || !this.mainForm || !this.rows.length) return;

      this.listeners?.abort();
      this.listeners = new AbortController();
      const options = { signal: this.listeners.signal };
      this.addEventListener('change', (event) => this.onChange(event), options);
      this.addEventListener('input', (event) => {
        if (event.target.matches('[data-addon-quantity]')) this.updateQuantityButtons(event.target.closest('[data-addon-row]'));
      }, options);
      this.addEventListener('click', (event) => this.onQuantityClick(event), options);
      this.section.addEventListener('submit', (event) => this.onSubmit(event), { ...options, capture: true });
      this.rows.forEach((row) => this.updateVariant(row));
      this.setAttribute('data-ready', '');
      this.updateSelection();
    }

    disconnectedCallback() {
      this.listeners?.abort();
      this.section?.removeAttribute('data-pp-addons-selected');
    }

    onChange(event) {
      const row = event.target.closest('[data-addon-row]');
      if (!row) return;
      if (event.target.matches('[data-addon-variant]')) this.updateVariant(row);
      if (event.target.matches('[data-addon-quantity]')) this.updateQuantityButtons(row);
      this.setMessage('');
      this.updateSelection();
    }

    updateVariant(row) {
      const select = row.querySelector('[data-addon-variant]');
      const option = select.selectedOptions[0];
      const quantity = row.querySelector('[data-addon-quantity]');
      const check = row.querySelector('[data-addon-check]');
      const available = option?.dataset.available === 'true';
      check.disabled = !available;
      if (!available) check.checked = false;
      select.disabled = !Array.from(select.options).some((item) => item.dataset.available === 'true');
      quantity.disabled = !available;
      quantity.min = option?.dataset.min || '1';
      quantity.step = option?.dataset.step || '1';
      if (option?.dataset.max) quantity.max = option.dataset.max;
      else quantity.removeAttribute('max');

      const min = Number(quantity.min);
      const step = Number(quantity.step);
      const maximum = quantity.max ? Number(quantity.max) : Infinity;
      const requested = Number(quantity.value) || min;
      const aligned = min + Math.max(0, Math.floor((requested - min) / step)) * step;
      quantity.value = Math.max(min, Math.min(aligned, maximum));
      row.querySelector('[data-addon-unavailable]').hidden = available;
      if (option) row.querySelector('[data-addon-price]').textContent = option.dataset.price;
      const image = row.querySelector('.pp-product-addons__image img');
      const manualImage = row.querySelector('.pp-product-addons__image')?.dataset.manualImage === 'true';
      if (image && option?.dataset.image && !manualImage) {
        image.src = option.dataset.image;
        image.removeAttribute('srcset');
      }
      this.updateQuantityButtons(row);
    }

    updateQuantityButtons(row) {
      const quantity = row.querySelector('[data-addon-quantity]');
      const current = Number(quantity.value);
      row.querySelector('[data-addon-minus]').disabled = quantity.disabled || current <= Number(quantity.min);
      row.querySelector('[data-addon-plus]').disabled = quantity.disabled || (quantity.max && current >= Number(quantity.max));
    }

    onQuantityClick(event) {
      const button = event.target.closest('[data-addon-minus], [data-addon-plus]');
      if (!button || button.disabled) return;
      const row = button.closest('[data-addon-row]');
      const quantity = row.querySelector('[data-addon-quantity]');
      if (button.hasAttribute('data-addon-plus')) quantity.stepUp();
      else quantity.stepDown();
      quantity.dispatchEvent(new Event('change', { bubbles: true }));
    }

    selectedRows() {
      return this.rows.filter((row) => row.querySelector('[data-addon-check]').checked);
    }

    updateSelection() {
      this.rows.forEach((row) => row.toggleAttribute('data-selected', row.querySelector('[data-addon-check]').checked));
      const selected = this.selectedRows().length > 0;
      this.section.toggleAttribute('data-pp-addons-selected', selected);
      this.querySelector('[data-addon-checkout-note]').hidden = !selected;
    }

    setMessage(text, isError = false) {
      const message = this.querySelector('[data-addon-message]');
      message.textContent = text;
      message.hidden = !text;
      message.toggleAttribute('data-error', isError);
      message.setAttribute('role', isError ? 'alert' : 'status');
    }

    async onSubmit(event) {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      const formId = form.getAttribute('id');
      if (formId !== this.dataset.formId && formId !== this.dataset.stickyFormId) return;
      if (this.pending) {
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }
      const selected = this.selectedRows();
      if (!selected.length) return;

      // Only handle the optional multi-product purchase; normal submissions remain native.
      event.preventDefault();
      event.stopImmediatePropagation();
      this.setMessage('');
      const invalidRow = selected.find((row) => !row.querySelector('[data-addon-quantity]').checkValidity());
      if (invalidRow) {
        this.setMessage(this.dataset.invalidQuantity, true);
        invalidRow.querySelector('[data-addon-quantity]').focus();
        return;
      }
      if (typeof window.validateForm === 'function') {
        const invalid = window.validateForm(this.mainForm.closest('.m-main-product--info'));
        if (invalid?.length) {
          this.setMessage(this.dataset.requiredFields, true);
          return;
        }
      }

      const submitted = new FormData(form);
      const main = new FormData(this.mainForm);
      const variantId = submitted.get('id');
      const quantity = Number(submitted.get('quantity') ?? 1);
      if (!variantId || !Number.isSafeInteger(quantity) || quantity < 1) {
        this.setMessage(this.dataset.requiredFields, true);
        return;
      }
      const mainQuantity = form.querySelector('[name="quantity"]');
      if (mainQuantity && !mainQuantity.checkValidity()) {
        mainQuantity.reportValidity();
        return;
      }

      const payload = new FormData();
      payload.append('items[0][id]', variantId);
      payload.append('items[0][quantity]', String(quantity));
      if (main.get('selling_plan')) payload.append('items[0][selling_plan]', main.get('selling_plan'));
      for (const [key, value] of main.entries()) {
        if (key.startsWith('properties[')) payload.append(key.replace(/^properties/, 'items[0][properties]'), value);
      }
      selected.forEach((row, index) => {
        payload.append(`items[${index + 1}][id]`, row.querySelector('[data-addon-variant]').value);
        payload.append(`items[${index + 1}][quantity]`, row.querySelector('[data-addon-quantity]').value);
      });

      const cart = document.querySelector('m-cart-drawer');
      const sections = cart?.getSectionsToRender?.() || [];
      const sectionIds = Array.from(new Set(sections.map((section) => section.id))).slice(0, 5);
      if (sectionIds.length) {
        payload.append('sections', sectionIds.join(','));
        payload.append('sections_url', window.location.pathname + window.location.search);
      }
      const formElement = form.closest('product-form');
      const buttons = Array.from(this.section.querySelectorAll('button[name="add"]'));
      const buttonStates = buttons.map((button) => button.disabled);
      const controls = Array.from(this.querySelectorAll('input, select, button'));
      const controlStates = controls.map((control) => control.disabled);
      this.pending = true;
      buttons.forEach((button) => { button.disabled = true; });
      controls.forEach((control) => { control.disabled = true; });
      formElement?.toggleSpinner?.(true);

      try {
        const settings = window.MinimogSettings || {};
        const root = window.Shopify?.routes?.root || '/';
        const response = await fetch(settings.routes?.cart_add_url || `${root}cart/add.js`, {
          method: 'POST',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: payload,
        });
        const result = await response.json();
        if (!response.ok || result.status) {
          const message = typeof result.description === 'string' ? result.description : result.message;
          this.setMessage(message ? `${message} ${this.dataset.error}` : this.dataset.error, true);
          try {
            const errorEvent = window.MinimogTheme?.pubSubEvents?.cartError;
            if (errorEvent) window.MinimogEvents?.emit(errorEvent, { source: 'product-form', productVariantId: variantId, errors: result.description, message: result.message });
          } catch { /* Keep Shopify's stock feedback if an app listener fails. */ }
          cart?.onCartDrawerUpdate?.();
          return;
        }

        this.setMessage(this.dataset.success);
        const firstItem = result.items?.[0];
        const cartResult = firstItem ? { ...result, id: firstItem.id, variant_id: firstItem.variant_id, product_id: firstItem.product_id } : result;
        const canRender = sectionIds.length && sectionIds.every((id) => typeof result.sections?.[id] === 'string');
        if (settings.use_ajax_atc && settings.enable_cart_drawer && cart && canRender) {
          try {
            const updateEvent = window.MinimogTheme?.pubSubEvents?.cartUpdate;
            if (updateEvent) window.MinimogEvents?.emit(updateEvent, cartResult);
            cart.renderContents(cartResult);
          } catch {
            window.location.assign(settings.routes?.cart || `${root}cart`);
          }
        } else {
          window.location.assign(settings.routes?.cart || `${root}cart`);
        }
      } catch {
        this.setMessage(this.dataset.error, true);
      } finally {
        this.pending = false;
        buttons.forEach((button, index) => { button.disabled = buttonStates[index] || button.classList.contains('disabled'); });
        controls.forEach((control, index) => { control.disabled = controlStates[index]; });
        formElement?.toggleSpinner?.(false);
      }
    }
  }

  customElements.define('pp-product-addons', PPProductAddons);
})();
